import { useQuery } from "@tanstack/react-query";
import type { QueryKey, UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CasinoGamesProvider,
  CasinoProvidersResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";

export const GAME_PASS_CASINO_PROVIDERS_QUERY_KEY = [
  "game-pass",
  "casino-providers",
] as const;

export type CasinoProvidersResult = {
  providers: CasinoGamesProvider[];
  totalGames: number;
};

/** Pull the `{ providers, total_games }` payload out of the response whether
 *  it arrives bare or wrapped in the standard `{ success, data }` envelope. */
const unwrapProviders = (payload: unknown): CasinoProvidersResult => {
  const root =
    payload && typeof payload === "object"
      ? ((payload as Record<string, unknown>).data &&
        typeof (payload as Record<string, unknown>).data === "object" &&
        !Array.isArray((payload as Record<string, unknown>).data)
          ? ((payload as Record<string, unknown>).data as Record<string, unknown>)
          : (payload as Record<string, unknown>))
      : {};
  const providers = Array.isArray(root.providers)
    ? (root.providers as CasinoGamesProvider[])
    : [];
  const totalGames =
    typeof root.total_games === "number" ? root.total_games : providers.length;
  return { providers, totalGames };
};

/**
 * Fetch the casino provider list powering the Create Task casino picker from
 * `GET /game-pass-admin/v2/admin/casino-games/providers`. Returns the providers
 * (each with its total / active game counts) plus the catalogue-wide
 * `total_games`. An absent / malformed payload resolves to an empty list rather
 * than throwing, so the picker degrades to an empty state instead of erroring.
 */
export const fetchCasinoProviders =
  async (): Promise<CasinoProvidersResult> => {
    const response = await getPrivateInstance().get<CasinoProvidersResponse>(
      API_ROUTES.GET_GAME_PASS_CASINO_PROVIDERS,
    );
    return unwrapProviders(response.data);
  };

export const getCasinoProvidersQueryOptions = (): {
  queryKey: QueryKey;
  queryFn: typeof fetchCasinoProviders;
  staleTime: number;
  retry: typeof retryOnTransientErrors;
} => ({
  queryKey: [...GAME_PASS_CASINO_PROVIDERS_QUERY_KEY],
  // The provider catalogue changes rarely, so cache it for a few minutes to
  // avoid a refetch every time the admin opens the casino picker.
  staleTime: 1000 * 60 * 5,
  queryFn: fetchCasinoProviders,
  retry: retryOnTransientErrors,
});

export const useGetCasinoProviders = (
  enabled = true,
): UseQueryResult<CasinoProvidersResult, Error> =>
  useQuery<CasinoProvidersResult, Error>({
    ...getCasinoProvidersQueryOptions(),
    enabled,
    refetchOnWindowFocus: false,
  });
